/* eslint-disable no-console */
import React, {
  createContext,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from "react";
import useAuth from "common/hooks/useAuth";
import usePersistedState from "common/hooks/usePersistedState";
import usePark from "common/hooks/usePark";
import { Animal } from "types/Animal";
import fetchJSON from "common/utils/fetchJSON";
import qs from "qs";
import { parseStrapiFormat } from "common/utils/strapi";

type AnimalProps = {
  animals?: Animal[];
  refreshAnimals: () => Promise<any>;
  isFetching: boolean;
};

export const AnimalContext = createContext<AnimalProps>({} as AnimalProps);

type Props = {
  children: React.ReactNode;
};

export function AnimalProvider({ children }: Props) {
  const { user: currentUser } = useAuth();
  const { park } = usePark();
  const canManageAnimals = currentUser?.isAdmin || currentUser?.isSuperadmin;
  const [animals, setAnimals] = usePersistedState(null, "animals");
  const [isFetching, setIsFetching] = useState(false);

  const getUrl = useCallback(
    (baseUrl: string, parkId: string) => {
      const query: any = {
        sort: ["houseName:ASC"],
        filters: {},
        pagination: {
          page: 1,
          pageSize: 5000,
        },
      };

      if (park) {
        query.filters.park = { $eq: parkId };
      }

      const queryParams = qs.stringify(query, { encodeValuesOnly: true });
      return `${baseUrl}${queryParams ? "?" : ""}${queryParams}`;
    },
    [park]
  );

  const refreshAnimals = useCallback(async () => {
    try {
      setIsFetching(true);

      if (park && park.id) {
        const url = getUrl("animals/data/list/all", park.id);

        const res = await fetchJSON({ url, method: "GET" });
        if (res) {
          setAnimals(res ? parseStrapiFormat(res) : undefined);

          return res;
        }
      } else {
        setAnimals([]);
      }

      return false;
    } catch (e) {
      console.log(e);
      return false;
    } finally {
      setIsFetching(false);
    }
  }, [getUrl, park, setAnimals]);

  // useEffect(() => {
  //   const firstLoad = async () => {
  //     if (currentUser && isFirstLoad) {
  //       const res = await refreshAnimals();
  //       if (res) {
  //         setIsFirstLoad(false);
  //       }
  //     }
  //   };

  //   firstLoad();
  // }, [currentUser, isFirstLoad, refreshAnimals, park?.id]);

  useEffect(() => {
    if (park && park?.id) {
      refreshAnimals();
    }
  }, [park, refreshAnimals]);

  const value: AnimalProps = useMemo(
    () => ({
      animals,
      setAnimals,
      refreshAnimals,
      isFetching,
      canManageAnimals,
    }),
    [animals, setAnimals, refreshAnimals, isFetching, canManageAnimals]
  );

  return (
    <AnimalContext.Provider value={value}>{children}</AnimalContext.Provider>
  );
}
